import React, {useState, useEffect} from "react";
import { Dialog, Classes, Checkbox, Icon } from "@blueprintjs/core";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {isEmail} from 'validator';

// Components
import Button from "../common/components/button";
import SelectInput from "../common/components/select-input";
import TextInput from "../common/components/text-input";
import Tooltip from "../common/components/tooltip";
import FeaturesSelector from "./features-selector";

// Actions
import {createUser} from "./users-action";
import {readClients} from "../clients/clients-actions";

// Hooks
import useInputValidation from "../common/hooks/use-input-validation";

// Selectors
import {clientsSelector} from "../clients/clients-selectors";

// Component Selector
const newUserDialogPageSelector = createSelector(
    clientsSelector,
    (clients) => {
        return {
            clients: clients?.toJS()?.map(d => ({
                value: d._id,
                label: d.name
            })) || []
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        createUser: (user, sendWelcomeEmail) => dispatch(createUser(user, sendWelcomeEmail)),
        readClients: () => dispatch(readClients())
    }
};

const NewUserDialog = (props) => {

    // Props
    const {isOpen, onClose} = props;

    // Redux
    const {createUser, readClients} = useReduxDispatch();
    const {clients} = useSelector(state => newUserDialogPageSelector(state));

    // i18n
    const {t} = useTranslation("users");

    // Form state
    const [nameData, setName] = useInputValidation();
    const [surname, setSurname] = useState("");
    const [emailData, setEmail] = useInputValidation({
        validationFn: value => !!isEmail(value),
        errMessage: t("common:invalidEmail")
    });
    const [userTypeData, setUserType] = useInputValidation();
    const [clientData, setClient] = useInputValidation();
    const [features, setFeatures] = useState([]);

    // state of checkbox send email
    const [userEmailChecked, setUserEmailChecked] = useState(false);

    // Can submit
    const [canSubmit, setCanSubmit] = useState(false);

    // get clients
    useEffect(() => {
        readClients();
    }, []);

    // Check canSubmit
    useEffect(() => {
        setCanSubmit(
            nameData.isValid &&
            emailData.isValid &&
            userTypeData.isValid &&
            (userTypeData?.value === "admin" ? true : !!clientData?.value)
        );
    }, [nameData, emailData, userTypeData, clientData]);

    function cleanFormState() {
        setName("");
        setSurname("");
        setEmail("");
        setUserEmailChecked(false);
        setUserType("");
        setClient("");
        setFeatures([]);
    }

    const userTypeOptions = [{
        value: "client",
        label: t("userType.client")
    }, {
        value: "admin",
        label: t("userType.admin")
    }];

    const langOptions = [
        {
            value: "es",
            label: t("profile:langOptions.es")
        },
        {
            value: "en",
            label: t("profile:langOptions.en")
        }
    ];

    // set language selected
    const [selectedLanguage, setSelectedLanguage] = useState(langOptions[0].value);

    // user language selected
    const selectedLanguageData = langOptions.find(langOptions => langOptions.value === selectedLanguage);

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            lazy={true}
            style={{
                backgroundColor: "white",
                paddingBottom: "40px"
            }}
        >
            <div
                style={{margin: "20px 40px"}}
                className={Classes.DIALOG_BODY}
            >
                <h3 style={{margin: "20px 0"}}>{t("newUserDialog.title")}</h3>
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between"
                }}>
                    <TextInput
                        label={t("newUserDialog.input.name")}
                        value={nameData.value}
                        setValue={setName}
                        style={inputStyle}
                        placeholder={t("newUserDialog.input.placeholder_name")}
                        validation
                        isValid={nameData.isValid}
                        errorMessage={nameData.errorMessage}
                    />
                    <TextInput
                        label={t("newUserDialog.input.surname")}
                        value={surname}
                        setValue={setSurname}
                        style={inputStyle}
                        placeholder={t("newUserDialog.input.placeholder_surname")}
                        validation
                    />
                    <TextInput
                        label={t("newUserDialog.input.email")}
                        value={emailData.value}
                        setValue={setEmail}
                        style={inputStyle}
                        placeholder={t("newUserDialog.input.placeholder_email")}
                        validation
                        isValid={emailData.isValid}
                        errorMessage={emailData.errorMessage}
                    />
                    <div style={{
                        width: "calc(50% - 10px)",
                        display: "flex"
                    }}>
                        <Checkbox
                            label={t("sendEmail")}
                            checked={userEmailChecked}
                            onChange={() => setUserEmailChecked(!userEmailChecked)}
                            style={{
                                margin: "auto 10px auto 0",
                                fontSize: "12px"
                            }}
                        />
                        <div style={{margin: "auto"}}>
                            <Tooltip
                                position={"top-start"}
                                content={
                                    <div style={{
                                        maxWidth: "400px"
                                    }}>
                                        {t("tooltip.newUserSendWelcomeEmail")}
                                    </div>
                                }
                            >
                                <Icon icon="help" iconSize={20} />
                            </Tooltip>
                        </div>
                    </div>
                    <SelectInput
                        options={userTypeOptions}
                        label={t("newUserDialog.input.type")}
                        placeholder={t("userTypePlaceholder")}
                        onChange={(selectedUserType) => setUserType(selectedUserType.value)}
                        value={userTypeOptions.find(userType => userType.value === userTypeData.value)}
                        style={{...inputStyle}}
                        validation
                        isValid={userTypeData.isValid}
                        errorMessage={userTypeData.errorMessage}
                    />
                    {userTypeData?.value === "client" && (
                        <SelectInput
                            options={clients}
                            label={t("newUserDialog.input.clients")}
                            placeholder={t("common:selectClientPlaceholder")}
                            onChange={(newSelectedClient) => setClient(newSelectedClient.value)}
                            value={clients.find(client => client.value === clientData.value)}
                            style={{...inputStyle}}
                            validation
                            isValid={clientData.isValid}
                            errorMessage={clientData.errorMessage}
                        />
                    )}
                    {userTypeData?.value === "client" && (
                        <FeaturesSelector features={features} onChange={setFeatures} />
                    )}
                    <SelectInput
                        options={langOptions}
                        label={t("profile:inputs.lang")}
                        onChange={(selectedLanguage) => setSelectedLanguage(selectedLanguage.value)}
                        value={selectedLanguageData}
                        style={{...inputStyle}}
                    />
                </div>
            </div>
            <div
                style={{margin: "20px 40px 0 40px"}}
                className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Button
                        text={t("newUserDialog.input.cancel")}
                        action={() => {
                            onClose();
                            cleanFormState();
                        }}
                        intent={"cancel"}
                        style={{
                            marginRight: "15px",
                        }}
                    />
                    <Button
                        text={t("newUserDialog.title")}
                        action={() => {

                            let newUser = {
                                name: nameData.value,
                                surnames: surname,
                                email: emailData.value,
                                type: userTypeData.value,
                                lang: selectedLanguageData.value
                            };

                            if (userTypeData.value === "client") {
                                newUser = {
                                    ...newUser,
                                    client: clientData.value,
                                    features: features
                                };
                            }
                            createUser(newUser, userEmailChecked);
                            onClose();
                            cleanFormState();

                        }}
                        intent={"secondary"}
                        disabled={canSubmit}
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default NewUserDialog;

const inputStyle = {
    marginBottom: "15px",
    width: "calc(50% - 10px)"
};